import React from 'react';
import BenefitsSection from './common/BenefitsSection';
import ProductCTA from './common/ProductCTA';
import PageSEO from './common/PageSEO';
import ClientLogos from './ClientLogos';
import { useT } from '../i18n';

const T = {
  es: {
    seo: {
      title: 'Cadenia Intelligence - Supply Chain Analytics y Agentes de IA',
      description: 'Transforme datos logísticos en decisiones estratégicas. Analytics avanzado, dashboards en tiempo real, agente conversacional con IA y optimización para su red de transporte.',
      keywords: 'supply chain analytics, inteligencia artificial logística, dashboards transporte, KPIs logísticos, AI analyst, optimización rutas, análisis cadena suministro',
    },
    benefits: [
      {
        title: 'Reducción de Costes',
        description: 'Identifique ineficiencias en su red de transporte y optimice asignaciones para reducir costes operativos con datos reales.',
      },
      {
        title: 'Mejora del Nivel de Servicio',
        description: 'Detecte patrones de retraso, mida la puntualidad por transportista y tome acciones preventivas basadas en datos.',
      },
      {
        title: 'De Horas a Minutos',
        description: 'Obtenga insights que antes requerían horas de análisis manual. Pregunte al agente de IA y reciba respuestas inmediatas.',
      },
    ],
    hero: {
      badge: 'CADENIA INTELLIGENCE',
      subtitle: 'Convierta los datos de su operación logística en decisiones. Analytics avanzado, agentes de IA y optimización para medir, entender y mejorar el rendimiento de su red de transporte.',
      cta: 'Ver Demo de Intelligence',
    },
    shell: {
      module: 'Módulo',
      sidebarItems: ['Análisis', 'Agentes IA', 'Informes', 'Optimización'],
      sidebarFooter: ['Soporte', 'Mi Perfil'],
      heading: 'ANÁLISIS DE ENVÍOS BI',
      controls: 'Controles',
      th: {
        region: 'REGIÓN',
        shipments: 'ENVÍOS',
        transit: 'T. TRÁNSITO',
        transitShort: 'T. TRÁN.',
        orderDelivery: 'PEDIDO→ENTREGA',
        trace: 'TRAZAB.',
      },
      rows: [
        { region: 'Andalucía', envios: '312', transit: '1.52', delivery: '2.40', h24: '32%', h48: '48%', h72: '16%', ontime: '97%', trace: '96%' },
        { region: 'Aragón', envios: '156', transit: '1.18', delivery: '1.95', h24: '45%', h48: '42%', h72: '11%', ontime: '98%', trace: '93%' },
        { region: 'Castilla y León', envios: '208', transit: '1.35', delivery: '2.10', h24: '40%', h48: '44%', h72: '13%', ontime: '95%', trace: '91%' },
        { region: 'Cataluña', envios: '189', transit: '1.10', delivery: '1.80', h24: '52%', h48: '38%', h72: '8%', ontime: '99%', trace: '97%' },
        { region: 'C. Valenciana', envios: '145', transit: '1.22', delivery: '2.05', h24: '42%', h48: '45%', h72: '10%', ontime: '96%', trace: '94%' },
      ],
    },
    sources: {
      badge: 'FUENTES DE DATOS',
      titleTop: 'Todos sus datos,',
      titleHighlight: 'un solo análisis',
      paragraph: 'Cadenia Intelligence agrega información de cualquier origen — visibilidad, documentos, ERPs, ficheros o formularios — en un modelo analítico unificado.',
      items: [
        { proto: 'API', name: 'Cadenia Visibility', desc: 'Envíos, rutas, eventos' },
        { proto: 'API', name: 'Cadenia Documents', desc: 'eCMR, firmas, PODs' },
        { proto: 'API', name: 'ERP / TMS', desc: 'Pedidos, costes, clientes' },
        { proto: 'CSV', name: 'Excel / CSV', desc: 'Importaciones manuales' },
        { proto: 'SQL', name: 'Base de datos', desc: 'Conexión directa' },
        { proto: 'WEB', name: 'Formularios', desc: 'Datos de campo' },
      ],
      pipeline: 'Normalización · Enriquecimiento · Análisis',
      outputs: ['Dashboards', 'Agentes IA', 'Informes', 'Optimizadores'],
    },
    bi: {
      title: 'Supply Chain Analytics',
      paragraph: 'Dashboards interactivos con KPIs de nivel de servicio, volúmenes, costes y trazabilidad. Filtre por transportista, cliente, región, modo de transporte o periodo para obtener la vista exacta que necesita.',
      bullets: [
        'KPIs de nivel de servicio, volúmenes y costes',
        'Mapas de calor y distribución geográfica',
        'Segmentación y filtros avanzados multidimensionales',
        'Evolución temporal y comparativa de periodos',
      ],
      months: ['Ene', 'Feb', 'Mar', 'Abr'],
    },
    agents: {
      title: 'Agentes de IA',
      paragraph: 'Analistas conversacionales que responden preguntas en lenguaje natural sobre su operación logística. Pregunte lo que necesite y obtenga respuestas con datos, tablas y gráficos en segundos.',
      bullets: [
        'Consultas en lenguaje natural',
        'Respuestas con tablas, métricas y resúmenes ejecutivos',
        'Agentes especializados por área de negocio',
        'Análisis comparativo de periodos',
      ],
      panelTitle: 'Agentes IA',
      panelSubtitle: 'Selecciona un agente',
      agentName: 'ANALISTA LOGÍSTICO',
      userMsg: 'Tiempos de entrega por zonas de España',
      responseTitle: 'Análisis de Tiempos de Entrega por Zonas',
      resp: {
        p1: 'Se han procesado ',
        shipments: '473 envíos',
        p2: ' con ',
        tons: '3.784 t',
        p3: '. Puntualidad del ',
        pct: '94%',
        p4: '.',
      },
      th: { region: 'REGIÓN', shipments: 'ENVÍOS', transit: 'TRÁNSITO', h24: '% 24H' },
      rows: [
        { region: 'Castilla y León', envios: '84', transit: '0,52 d', pct: '93,8%' },
        { region: 'C.-La Mancha', envios: '51', transit: '0,24 d', pct: '100%' },
        { region: 'Andalucía', envios: '67', transit: '0,89 d', pct: '87,2%' },
      ],
      placeholder: 'Escribe tu consulta...',
    },
    reports: {
      title: 'Informes Periódicos',
      paragraph: 'Informes generados automáticamente con la frecuencia que necesite. Configurables en tono, extensión e idioma, pensados para que lleguen directamente a la dirección o al equipo operativo sin intervención manual.',
      bullets: [
        'Generación automática programada',
        'Tono ejecutivo u operativo, extensión configurable',
        'Historial completo y generación bajo demanda',
        'Distribución por email automática',
      ],
      docTitle: 'Informe Mensual de Rendimiento Logístico',
      docDesc: 'Análisis del rendimiento operacional: tiempos, costes y eficiencia para la dirección ejecutiva.',
      config: 'Configuración',
      configRows: [
        { key: 'Frecuencia', val: 'Mensual' },
        { key: 'Tono', val: 'Ejecutivo' },
        { key: 'Extensión', val: 'Extenso' },
        { key: 'Idioma', val: 'ES' },
      ],
      schedule: 'Programación',
      active: 'Activo',
      frequency: '· Mensual',
      history: 'Historial de informes',
      historyRows: [
        { range: '2026-03-01 — 2026-03-31', date: '1 abr 2026 · 23s', status: 'completado', style: 'bg-green-50 text-green-600 border-green-200' },
        { range: '2026-02-01 — 2026-02-28', date: '1 mar 2026 · 19s', status: 'completado', style: 'bg-green-50 text-green-600 border-green-200' },
        { range: '2026-01-01 — 2026-01-31', date: '1 feb 2026 · 21s', status: 'completado', style: 'bg-green-50 text-green-600 border-green-200' },
        { range: '2025-12-01 — 2025-12-31', date: '1 ene 2026 · 18s', status: 'completado', style: 'bg-green-50 text-green-600 border-green-200' },
      ],
    },
    optimizers: {
      title: 'Optimizadores Personalizados',
      p1: 'Motores de decisión que combinan inteligencia artificial, programación determinista y optimización matemática para automatizar flujos operativos complejos.',
      p2: 'Cada optimizador se diseña a medida del problema: desde la clasificación automática de emails entrantes hasta la asignación de transporte por coste óptimo, pasando por la planificación de rutas o la distribución de carga entre proveedores. El mismo motor, diferentes reglas de negocio.',
      bullets: [
        'IA + reglas de negocio + optimización matemática',
        'Asignación de transporte por coste óptimo',
        'Clasificación y enrutamiento automático de comunicaciones',
        'Diseñados a medida para cada caso de uso',
      ],
      panelTitle: 'Optimizadores',
      panelSubtitle: 'Motores de decisión activos',
      items: [
        { name: 'Asignación de Transporte', type: 'Optimización matemática', status: 'Activo', statusStyle: 'bg-green-50 text-green-600 border-green-200', desc: 'Coste óptimo por envío según tarifas, capacidad y SLA', runs: '1.240 ejecuciones', saving: '↓ 12% coste medio' },
        { name: 'Clasificador de Emails', type: 'IA + Reglas', status: 'Activo', statusStyle: 'bg-green-50 text-green-600 border-green-200', desc: 'Categorización y enrutamiento automático de comunicaciones', runs: '8.430 emails', saving: '97% precisión' },
        { name: 'Planificación de Rutas', type: 'Programación determinista', status: 'En desarrollo', statusStyle: 'bg-amber-50 text-amber-600 border-amber-200', desc: 'Secuenciación de paradas por ventana horaria y capacidad', runs: '—', saving: '—' },
      ],
    },
    ctaFeatures: [
      'Supply Chain Analytics',
      'Agentes de IA',
      'Informes Periódicos',
      'Optimizadores Personalizados',
    ],
  },
  en: {
    seo: {
      title: 'Cadenia Intelligence - Supply Chain Analytics and AI Agents',
      description: 'Turn logistics data into strategic decisions. Advanced analytics, real-time dashboards, a conversational AI agent and optimization for your transport network.',
      keywords: 'supply chain analytics, logistics artificial intelligence, transport dashboards, logistics KPIs, AI analyst, route optimization, supply chain analysis',
    },
    benefits: [
      {
        title: 'Cost Reduction',
        description: 'Identify inefficiencies across your transport network and optimize assignments to cut operating costs with real data.',
      },
      {
        title: 'Higher Service Levels',
        description: 'Detect delay patterns, measure on-time performance by carrier and take preventive action based on data.',
      },
      {
        title: 'From Hours to Minutes',
        description: 'Get insights that used to take hours of manual analysis. Ask the AI agent and get answers instantly.',
      },
    ],
    hero: {
      badge: 'CADENIA INTELLIGENCE',
      subtitle: 'Turn your logistics data into decisions. Advanced analytics, AI agents and optimization to measure, understand and improve the performance of your transport network.',
      cta: 'View Intelligence Demo',
    },
    shell: {
      module: 'Module',
      sidebarItems: ['Analytics', 'AI Agents', 'Reports', 'Optimization'],
      sidebarFooter: ['Support', 'My Profile'],
      heading: 'BI SHIPMENT ANALYTICS',
      controls: 'Controls',
      th: {
        region: 'REGION',
        shipments: 'SHIPMENTS',
        transit: 'TRANSIT T.',
        transitShort: 'TRANSIT',
        orderDelivery: 'ORDER→DELIVERY',
        trace: 'TRACE.',
      },
      rows: [
        { region: 'Andalusia', envios: '312', transit: '1.52', delivery: '2.40', h24: '32%', h48: '48%', h72: '16%', ontime: '97%', trace: '96%' },
        { region: 'Aragon', envios: '156', transit: '1.18', delivery: '1.95', h24: '45%', h48: '42%', h72: '11%', ontime: '98%', trace: '93%' },
        { region: 'Castile and León', envios: '208', transit: '1.35', delivery: '2.10', h24: '40%', h48: '44%', h72: '13%', ontime: '95%', trace: '91%' },
        { region: 'Catalonia', envios: '189', transit: '1.10', delivery: '1.80', h24: '52%', h48: '38%', h72: '8%', ontime: '99%', trace: '97%' },
        { region: 'Valencia', envios: '145', transit: '1.22', delivery: '2.05', h24: '42%', h48: '45%', h72: '10%', ontime: '96%', trace: '94%' },
      ],
    },
    sources: {
      badge: 'DATA SOURCES',
      titleTop: 'All your data,',
      titleHighlight: 'one unified analysis',
      paragraph: 'Cadenia Intelligence aggregates information from any source — visibility, documents, ERPs, files or forms — into a unified analytical model.',
      items: [
        { proto: 'API', name: 'Cadenia Visibility', desc: 'Shipments, routes, events' },
        { proto: 'API', name: 'Cadenia Documents', desc: 'eCMR, signatures, PODs' },
        { proto: 'API', name: 'ERP / TMS', desc: 'Orders, costs, customers' },
        { proto: 'CSV', name: 'Excel / CSV', desc: 'Manual imports' },
        { proto: 'SQL', name: 'Database', desc: 'Direct connection' },
        { proto: 'WEB', name: 'Forms', desc: 'Field data' },
      ],
      pipeline: 'Normalization · Enrichment · Analytics',
      outputs: ['Dashboards', 'AI Agents', 'Reports', 'Optimizers'],
    },
    bi: {
      title: 'Supply Chain Analytics',
      paragraph: 'Interactive dashboards with service level, volume, cost and traceability KPIs. Filter by carrier, customer, region, transport mode or time period to get exactly the view you need.',
      bullets: [
        'Service level, volume and cost KPIs',
        'Heatmaps and geographic distribution',
        'Advanced multidimensional segmentation and filters',
        'Time-series trends and period-over-period comparison',
      ],
      months: ['Jan', 'Feb', 'Mar', 'Apr'],
    },
    agents: {
      title: 'AI Agents',
      paragraph: 'Conversational analysts that answer natural-language questions about your logistics operation. Ask anything and get answers with data, tables and charts in seconds.',
      bullets: [
        'Natural-language queries',
        'Answers with tables, metrics and executive summaries',
        'Specialized agents per business area',
        'Comparative period analysis',
      ],
      panelTitle: 'AI Agents',
      panelSubtitle: 'Select an agent',
      agentName: 'LOGISTICS ANALYST',
      userMsg: 'Delivery times by region across Spain',
      responseTitle: 'Delivery Time Analysis by Region',
      resp: {
        p1: 'We processed ',
        shipments: '473 shipments',
        p2: ' totaling ',
        tons: '3,784 t',
        p3: '. On-time performance of ',
        pct: '94%',
        p4: '.',
      },
      th: { region: 'REGION', shipments: 'SHIPMENTS', transit: 'TRANSIT', h24: '% 24H' },
      rows: [
        { region: 'Castile and León', envios: '84', transit: '0.52 d', pct: '93.8%' },
        { region: 'C.-La Mancha', envios: '51', transit: '0.24 d', pct: '100%' },
        { region: 'Andalusia', envios: '67', transit: '0.89 d', pct: '87.2%' },
      ],
      placeholder: 'Type your question...',
    },
    reports: {
      title: 'Scheduled Reports',
      paragraph: 'Reports generated automatically at whatever frequency you need. Configurable in tone, length and language, designed to reach management or the operations team with no manual effort.',
      bullets: [
        'Automated scheduled generation',
        'Executive or operational tone, configurable length',
        'Full history and on-demand generation',
        'Automatic email distribution',
      ],
      docTitle: 'Monthly Logistics Performance Report',
      docDesc: 'Operational performance analysis: lead times, costs and efficiency for executive management.',
      config: 'Settings',
      configRows: [
        { key: 'Frequency', val: 'Monthly' },
        { key: 'Tone', val: 'Executive' },
        { key: 'Length', val: 'Detailed' },
        { key: 'Language', val: 'EN' },
      ],
      schedule: 'Schedule',
      active: 'Active',
      frequency: '· Monthly',
      history: 'Report history',
      historyRows: [
        { range: '2026-03-01 — 2026-03-31', date: 'Apr 1, 2026 · 23s', status: 'completed', style: 'bg-green-50 text-green-600 border-green-200' },
        { range: '2026-02-01 — 2026-02-28', date: 'Mar 1, 2026 · 19s', status: 'completed', style: 'bg-green-50 text-green-600 border-green-200' },
        { range: '2026-01-01 — 2026-01-31', date: 'Feb 1, 2026 · 21s', status: 'completed', style: 'bg-green-50 text-green-600 border-green-200' },
        { range: '2025-12-01 — 2025-12-31', date: 'Jan 1, 2026 · 18s', status: 'completed', style: 'bg-green-50 text-green-600 border-green-200' },
      ],
    },
    optimizers: {
      title: 'Custom Optimizers',
      p1: 'Decision engines that combine artificial intelligence, deterministic programming and mathematical optimization to automate complex operational workflows.',
      p2: 'Each optimizer is tailored to the problem at hand: from automatic classification of inbound emails to least-cost carrier assignment, route planning or load distribution across suppliers. The same engine, different business rules.',
      bullets: [
        'AI + business rules + mathematical optimization',
        'Least-cost transport assignment',
        'Automatic classification and routing of communications',
        'Custom-built for every use case',
      ],
      panelTitle: 'Optimizers',
      panelSubtitle: 'Active decision engines',
      items: [
        { name: 'Transport Assignment', type: 'Mathematical optimization', status: 'Active', statusStyle: 'bg-green-50 text-green-600 border-green-200', desc: 'Optimal cost per shipment based on rates, capacity and SLA', runs: '1,240 runs', saving: '↓ 12% avg cost' },
        { name: 'Email Classifier', type: 'AI + Rules', status: 'Active', statusStyle: 'bg-green-50 text-green-600 border-green-200', desc: 'Automatic categorization and routing of communications', runs: '8,430 emails', saving: '97% accuracy' },
        { name: 'Route Planning', type: 'Deterministic programming', status: 'In development', statusStyle: 'bg-amber-50 text-amber-600 border-amber-200', desc: 'Stop sequencing by time window and capacity', runs: '—', saving: '—' },
      ],
    },
    ctaFeatures: [
      'Supply Chain Analytics',
      'AI Agents',
      'Scheduled Reports',
      'Custom Optimizers',
    ],
  },
};

const IntelligenceDetail = () => {
  const t = useT(T);

  const benefits = t.benefits;



  return (
    <div className="pt-20">
      <PageSEO
        title={t.seo.title}
        description={t.seo.description}
        keywords={t.seo.keywords}
      />
      {/* Hero mejorado */}
      <section className="py-32 bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.08),transparent_50%)] animate-pulse" style={{animationDuration: '10s'}} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.06),transparent_50%)] animate-pulse" style={{animationDuration: '14s', animationDelay: '3s'}} />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 rounded-full text-sm font-medium text-indigo-700 mb-8">
              <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
              {t.hero.badge}
            </div>

            <h1 className="text-5xl md:text-7xl font-light text-slate-900 mb-6 leading-tight">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent font-medium">
                Cadenia
              </span>
              <span className="block font-light">Intelligence</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-600 max-w-4xl mx-auto leading-relaxed mb-12">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#demo" className="inline-flex items-center justify-center px-12 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200">
                {t.hero.cta}
              </a>
            </div>
          </div>

          {/* Ilustración — app shell estilo producto */}
          <div className="relative max-w-5xl mx-auto">
            <div className="absolute -inset-4 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl opacity-10 blur-lg"></div>
            <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
              <div className="flex">
                {/* Sidebar */}
                <div className="w-14 md:w-44 bg-slate-50 border-r border-slate-200 py-4 flex flex-col flex-shrink-0">
                  <div className="px-3 md:px-4 mb-5">
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-200 rounded-lg"></div>
                  </div>
                  <div className="px-3 md:px-4 mb-1 hidden md:block">
                    <span className="text-[9px] text-slate-400 uppercase tracking-wider font-medium">{t.shell.module}</span>
                  </div>
                  <div className="px-2 md:px-3 mb-1">
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-indigo-600 rounded-lg">
                      <div className="w-4 h-4 bg-white/30 rounded flex-shrink-0"></div>
                      <span className="text-[10px] text-white font-medium hidden md:block">Intelligence</span>
                      <svg className="w-3 h-3 text-white/60 ml-auto hidden md:block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                  <div className="px-2 md:px-3 space-y-0.5">
                    {t.shell.sidebarItems.map((item, i) => (
                      <div key={i} className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${i === 0 ? 'bg-indigo-50' : ''}`}>
                        <div className={`w-3.5 h-3.5 rounded flex-shrink-0 ${i === 0 ? 'bg-indigo-200' : 'bg-slate-200'}`}></div>
                        <span className={`text-[10px] hidden md:block ${i === 0 ? 'text-indigo-700 font-medium' : 'text-slate-500'}`}>{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-auto px-2 md:px-3 space-y-0.5">
                    {t.shell.sidebarFooter.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                        <div className="w-3.5 h-3.5 bg-slate-200 rounded flex-shrink-0"></div>
                        <span className="text-[10px] text-slate-400 hidden md:block">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Main content — Analytics BI dashboard */}
                <div className="flex-1 min-w-0">
                  <div className="px-5 md:px-8 pt-5 md:pt-6 pb-3">
                    <h3 className="text-sm md:text-base font-medium text-slate-800 mb-3">{t.shell.heading}</h3>
                    {/* Tabs */}
                    <div className="flex items-center gap-4 border-b border-slate-100 mb-4">
                      {['KPIs', 'Volumes', 'Service Level', 'Traceability'].map((tab, i) => (
                        <span key={i} className={`text-[10px] pb-2 ${i === 2 ? 'text-indigo-600 font-medium border-b-2 border-indigo-600' : 'text-slate-400'}`}>{tab}</span>
                      ))}
                    </div>
                    {/* Controls bar */}
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="text-[9px] text-slate-500">{t.shell.controls}</span>
                      {['Date Range', 'Period', 'Carrier', 'Mode', 'Origin Country'].map((ctrl, i) => (
                        <div key={i} className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[8px] text-slate-500 hidden md:block">
                          <span className="font-medium">{ctrl}</span> <span className="text-slate-400">All</span>
                        </div>
                      ))}
                    </div>
                    {/* KPI cards row 1 */}
                    <div className="grid grid-cols-6 gap-2 mb-3">
                      {[
                        { label: 'Shipments', value: '4,312' },
                        { label: 'Avg Transit Days', value: '1.40' },
                        { label: 'Avg Order to Transit', value: '0.85' },
                        { label: 'Avg Order to Delivery', value: '2.25' },
                        { label: '% On Time', value: '91%' },
                        { label: '% On Date', value: '96%' },
                      ].map((kpi, i) => (
                        <div key={i} className="bg-white rounded-lg p-2 border border-slate-100">
                          <div className="text-[7px] text-slate-400 uppercase tracking-wider mb-1">{kpi.label}</div>
                          <div className="text-sm md:text-base font-light text-slate-800">{kpi.value}</div>
                        </div>
                      ))}
                    </div>
                    {/* KPI cards row 2 — delivery breakdown */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {[
                        { label: '% 24H', value: '38%' },
                        { label: '% 48H', value: '44%' },
                        { label: '% 72H', value: '14%' },
                        { label: '% +72H', value: '4%' },
                      ].map((kpi, i) => (
                        <div key={i} className="bg-white rounded-lg p-2.5 border border-slate-100">
                          <div className="text-[7px] text-slate-400 uppercase tracking-wider mb-1">{kpi.label}</div>
                          <div className="text-lg md:text-xl font-light text-slate-800">{kpi.value}</div>
                        </div>
                      ))}
                    </div>
                    {/* Service Level table by region */}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="text-[9px] font-medium text-slate-500 px-3 py-1.5 bg-slate-50 border-b border-slate-200">Areas - Service Level</div>
                      {/* Desktop table */}
                      <div className="hidden md:block">
                        <div className="grid grid-cols-12 gap-0 px-3 py-1.5 bg-slate-50 border-b border-slate-200">
                          <span className="col-span-2 text-[7px] font-medium text-slate-500">{t.shell.th.region}</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">{t.shell.th.shipments}</span>
                          <span className="col-span-2 text-[7px] font-medium text-slate-500 text-right">{t.shell.th.transit}</span>
                          <span className="col-span-2 text-[7px] font-medium text-slate-500 text-right">{t.shell.th.orderDelivery}</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">% 24H</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">% 48H</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">% 72H</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">ON TIME</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">{t.shell.th.trace}</span>
                        </div>
                        {t.shell.rows.map((row, i) => (
                          <div key={i} className="grid grid-cols-12 gap-0 px-3 py-1.5 border-b border-slate-50 items-center">
                            <span className="col-span-2 text-[8px] text-slate-600">{row.region}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.envios}</span>
                            <span className="col-span-2 text-[8px] text-slate-600 font-mono text-right">{row.transit}</span>
                            <span className="col-span-2 text-[8px] text-slate-600 font-mono text-right">{row.delivery}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.h24}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.h48}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.h72}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.ontime}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.trace}</span>
                          </div>
                        ))}
                      </div>
                      {/* Mobile table — simplified */}
                      <div className="md:hidden">
                        <div className="grid grid-cols-6 gap-0 px-3 py-1.5 bg-slate-50 border-b border-slate-200">
                          <span className="col-span-2 text-[7px] font-medium text-slate-500">{t.shell.th.region}</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">{t.shell.th.shipments}</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">{t.shell.th.transitShort}</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">% 24H</span>
                          <span className="col-span-1 text-[7px] font-medium text-slate-500 text-right">% 48H</span>
                        </div>
                        {t.shell.rows.map((row, i) => (
                          <div key={i} className="grid grid-cols-6 gap-0 px-3 py-1.5 border-b border-slate-50 items-center">
                            <span className="col-span-2 text-[8px] text-slate-600">{row.region}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.envios}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.transit}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.h24}</span>
                            <span className="col-span-1 text-[8px] text-slate-600 font-mono text-right">{row.h48}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agregación de fuentes de datos */}
      <section className="py-24 bg-slate-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Text — centered */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-100 rounded-full text-xs font-medium text-indigo-700 mb-6">
              {t.sources.badge}
            </div>
            <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-6">
              {t.sources.titleTop}{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent font-medium">
                {t.sources.titleHighlight}
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t.sources.paragraph}
            </p>
          </div>

          {/* Visual diagram */}
          <div className="relative max-w-4xl mx-auto">
            {/* Source nodes — top grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12 relative">
              {t.sources.items.map((source, i) => (
                <div key={i} className="group relative">
                  <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all duration-300 relative z-10">
                    <div className="flex items-center gap-3">
                      <span className="text-[9px] font-mono font-medium text-indigo-400 bg-indigo-50 px-2 py-1 rounded border border-indigo-100">{source.proto}</span>
                      <div className="min-w-0">
                        <div className="text-sm text-slate-700 font-medium truncate">{source.name}</div>
                        <div className="text-[11px] text-slate-400 truncate">{source.desc}</div>
                      </div>
                    </div>
                  </div>
                  {/* Particle emitter — dots that float downward */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 z-0">
                    <div className="data-particle" style={{animationDelay: `${i * 0.6}s`}}></div>
                    <div className="data-particle" style={{animationDelay: `${i * 0.6 + 2}s`}}></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Central Cadenia node */}
            <div className="relative flex justify-center">
              {/* Radial gradient field */}
              <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[250px] bg-[radial-gradient(ellipse_at_center_bottom,rgba(99,102,241,0.1)_0%,transparent_65%)] pointer-events-none"></div>

              {/* Orbiting ring */}
              <div className="absolute -inset-3 pointer-events-none">
                <div className="w-full h-full rounded-full border border-indigo-100/60 animate-spin-very-slow"></div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-indigo-400 rounded-full shadow-sm shadow-indigo-400"></div>
              </div>

              <div className="relative">
                {/* Glow ring */}
                <div className="absolute -inset-4 bg-indigo-500/5 rounded-3xl blur-xl"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-indigo-500/20 rounded-2xl blur-sm animate-pulse" style={{animationDuration: '4s'}}></div>
                <div className="relative bg-white border-2 border-indigo-200 rounded-2xl px-8 py-6 shadow-lg text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div className="text-lg font-medium text-slate-800 mb-1">Cadenia Intelligence</div>
                  <div className="text-sm text-slate-500 mb-4">{t.sources.pipeline}</div>
                  <div className="flex items-center justify-center gap-6">
                    {t.sources.outputs.map((output, i) => (
                      <div key={i} className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${['bg-indigo-400', 'bg-purple-400', 'bg-blue-400', 'bg-teal-400'][i]}`}></div>
                        <span className="text-[11px] text-slate-500">{output}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .data-particle {
            position: absolute;
            width: 4px;
            height: 4px;
            background: #818cf8;
            border-radius: 50%;
            opacity: 0;
            animation: particle-fall 3.5s ease-in infinite;
            box-shadow: 0 0 6px rgba(129, 140, 248, 0.6);
          }
          @keyframes particle-fall {
            0% { transform: translateY(0); opacity: 0; }
            10% { opacity: 1; }
            70% { opacity: 0.6; }
            100% { transform: translateY(80px); opacity: 0; }
          }
          .animate-spin-very-slow {
            animation: spin 20s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </section>

      {/* BI — Supply Chain Analytics */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-8">
                {t.bi.title}
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {t.bi.paragraph}
              </p>
              <ul className="space-y-4">
                {t.bi.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-slate-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
                <div className="flex">
                  <div className="w-10 md:w-12 bg-slate-50 border-r border-slate-200 py-3 flex flex-col items-center gap-3 flex-shrink-0">
                    <div className="w-5 h-5 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-indigo-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                  </div>
                  <div className="flex-1 min-w-0 p-4 md:p-5">
                    {/* Tabs */}
                    <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-2">
                      {['KPIs', 'Volumes', 'Service Level', 'Traceability'].map((tab, i) => (
                        <span key={i} className={`text-[9px] pb-1 ${i === 0 ? 'text-indigo-600 font-medium border-b-2 border-indigo-600' : 'text-slate-400'}`}>{tab}</span>
                      ))}
                    </div>
                    {/* KPI row */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {[
                        { label: 'Shipments', value: '19,427' },
                        { label: 'Order to Transit', value: '4.16' },
                        { label: 'Transit to Delivery', value: '2.22' },
                        { label: 'Order to Delivery', value: '4.68' },
                      ].map((kpi, i) => (
                        <div key={i} className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                          <div className="text-[7px] text-slate-400 uppercase tracking-wider mb-0.5">{kpi.label}</div>
                          <div className="text-base font-light text-slate-800">{kpi.value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {[
                        { label: '% On Time', value: '88%' },
                        { label: '% On Date', value: '94%' },
                        { label: '% Traceability', value: '87%' },
                        { label: '% Completed', value: '82%' },
                      ].map((kpi, i) => (
                        <div key={i} className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                          <div className="text-[7px] text-slate-400 uppercase tracking-wider mb-0.5">{kpi.label}</div>
                          <div className="text-base font-light text-slate-800">{kpi.value}</div>
                        </div>
                      ))}
                    </div>
                    {/* Charts row */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Pie chart */}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <div className="text-[8px] text-slate-500 font-medium mb-2">Transit Days</div>
                        <div className="flex items-center gap-3">
                          <svg viewBox="0 0 36 36" className="w-16 h-16 flex-shrink-0">
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3"/>
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#818cf8" strokeWidth="3" strokeDasharray="38 62" strokeDashoffset="25"/>
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#6366f1" strokeWidth="3" strokeDasharray="30 70" strokeDashoffset="87"/>
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#4f46e5" strokeWidth="3" strokeDasharray="20 80" strokeDashoffset="57"/>
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#c7d2fe" strokeWidth="3" strokeDasharray="12 88" strokeDashoffset="37"/>
                          </svg>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-indigo-200 rounded-sm"></div><span className="text-[7px] text-slate-500">1-24H</span></div>
                            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-indigo-400 rounded-sm"></div><span className="text-[7px] text-slate-500">2-48H</span></div>
                            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-indigo-500 rounded-sm"></div><span className="text-[7px] text-slate-500">3-72H</span></div>
                            <div className="flex items-center gap-1"><div className="w-2 h-2 bg-indigo-600 rounded-sm"></div><span className="text-[7px] text-slate-500">4-&gt;72H</span></div>
                          </div>
                        </div>
                      </div>
                      {/* Line chart */}
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                        <div className="text-[8px] text-slate-500 font-medium mb-2">Shipments by Month</div>
                        <svg viewBox="0 0 120 50" className="w-full h-16">
                          <line x1="0" y1="48" x2="120" y2="48" stroke="#e2e8f0" strokeWidth="0.5"/>
                          <line x1="0" y1="32" x2="120" y2="32" stroke="#e2e8f0" strokeWidth="0.5"/>
                          <line x1="0" y1="16" x2="120" y2="16" stroke="#e2e8f0" strokeWidth="0.5"/>
                          <polyline points="10,38 40,22 70,12 100,18" fill="none" stroke="#818cf8" strokeWidth="1.5"/>
                          <circle cx="10" cy="38" r="2" fill="#818cf8"/>
                          <circle cx="40" cy="22" r="2" fill="#818cf8"/>
                          <circle cx="70" cy="12" r="2" fill="#818cf8"/>
                          <circle cx="100" cy="18" r="2" fill="#818cf8"/>
                          <text x="10" y="48" className="text-[4px]" fill="#94a3b8" textAnchor="middle">{t.bi.months[0]}</text>
                          <text x="40" y="48" className="text-[4px]" fill="#94a3b8" textAnchor="middle">{t.bi.months[1]}</text>
                          <text x="70" y="48" className="text-[4px]" fill="#94a3b8" textAnchor="middle">{t.bi.months[2]}</text>
                          <text x="100" y="48" className="text-[4px]" fill="#94a3b8" textAnchor="middle">{t.bi.months[3]}</text>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agentes IA */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="lg:order-2">
              <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-8">
                {t.agents.title}
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {t.agents.paragraph}
              </p>
              <ul className="space-y-4">
                {t.agents.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-slate-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:order-1 relative">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
                <div className="flex">
                  <div className="w-10 md:w-12 bg-slate-50 border-r border-slate-200 py-3 flex flex-col items-center gap-3 flex-shrink-0">
                    <div className="w-5 h-5 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-indigo-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-0 h-full">
                      {/* Agent selector */}
                      <div className="md:col-span-2 md:border-r border-b md:border-b-0 border-slate-100 px-4 py-4">
                        <div className="mb-3">
                          <div className="text-[11px] font-medium text-slate-700 mb-0.5">{t.agents.panelTitle}</div>
                          <div className="text-[9px] text-slate-400">{t.agents.panelSubtitle}</div>
                        </div>
                        <div className="border-2 border-indigo-200 bg-indigo-50/30 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                            </div>
                            <div>
                              <div className="text-[10px] font-medium text-slate-700">{t.agents.agentName}</div>
                              <div className="text-[8px] text-slate-400 font-mono">ID: a3f8...</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Chat */}
                      <div className="md:col-span-3 flex flex-col">
                        <div className="px-4 py-3 border-b border-slate-100">
                          <div className="text-[11px] font-medium text-slate-700">{t.agents.agentName}</div>
                        </div>
                        <div className="flex-1 px-4 py-3 space-y-3 overflow-hidden">
                          <div className="flex justify-end">
                            <div className="bg-indigo-600 text-white rounded-xl rounded-tr-sm px-3 py-2 max-w-[85%]">
                              <span className="text-[10px]">{t.agents.userMsg}</span>
                            </div>
                          </div>
                          <div className="bg-slate-50 rounded-xl rounded-tl-sm px-3 py-3 border border-slate-100">
                            <div className="text-[10px] font-medium text-slate-700 mb-2">{t.agents.responseTitle}</div>
                            <div className="text-[9px] text-slate-500 mb-3 leading-relaxed">
                              {t.agents.resp.p1}<span className="font-medium text-slate-700">{t.agents.resp.shipments}</span>{t.agents.resp.p2}<span className="font-medium text-slate-700">{t.agents.resp.tons}</span>{t.agents.resp.p3}<span className="font-medium text-slate-700">{t.agents.resp.pct}</span>{t.agents.resp.p4}
                            </div>
                            <div className="border border-slate-200 rounded overflow-hidden">
                              <div className="grid grid-cols-4 gap-0 bg-slate-100 px-2 py-1">
                                <span className="text-[8px] font-medium text-slate-500">{t.agents.th.region}</span>
                                <span className="text-[8px] font-medium text-slate-500">{t.agents.th.shipments}</span>
                                <span className="text-[8px] font-medium text-slate-500">{t.agents.th.transit}</span>
                                <span className="text-[8px] font-medium text-slate-500">{t.agents.th.h24}</span>
                              </div>
                              {t.agents.rows.map((row, i) => (
                                <div key={i} className="grid grid-cols-4 gap-0 px-2 py-1 border-t border-slate-100">
                                  <span className="text-[8px] text-slate-600">{row.region}</span>
                                  <span className="text-[8px] text-slate-600 font-mono">{row.envios}</span>
                                  <span className="text-[8px] text-slate-600">{row.transit}</span>
                                  <span className="text-[8px] text-slate-600 font-mono">{row.pct}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-100">
                          <div className="flex items-center gap-2 bg-slate-50 rounded-lg border border-slate-200 px-3 py-2">
                            <span className="text-[10px] text-slate-400 flex-1">{t.agents.placeholder}</span>
                            <div className="w-6 h-6 bg-indigo-500 rounded flex items-center justify-center flex-shrink-0">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informes Periódicos */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-8">
                {t.reports.title}
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                {t.reports.paragraph}
              </p>
              <ul className="space-y-4">
                {t.reports.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-slate-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
                <div className="flex">
                  <div className="w-10 md:w-12 bg-slate-50 border-r border-slate-200 py-3 flex flex-col items-center gap-3 flex-shrink-0">
                    <div className="w-5 h-5 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-indigo-200 rounded"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="px-4 md:px-6 pt-4 pb-3 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        <span className="text-xs font-medium text-slate-700">{t.reports.docTitle}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-0">
                      <div className="px-4 md:px-5 py-4 border-r border-slate-100">
                        <div className="text-[10px] text-slate-600 leading-relaxed mb-4">
                          {t.reports.docDesc}
                        </div>
                        <div className="space-y-2 mb-4">
                          <div className="text-[9px] font-medium text-slate-500 uppercase tracking-wider">{t.reports.config}</div>
                          {t.reports.configRows.map((row, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className="text-[10px] text-slate-500">{row.key}</span>
                              <span className="text-[10px] text-slate-700 font-medium">{row.val}</span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-slate-100 pt-3">
                          <div className="text-[9px] font-medium text-slate-500 uppercase tracking-wider mb-2">{t.reports.schedule}</div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[8px] bg-green-50 text-green-600 border border-green-200 px-1.5 py-0.5 rounded font-medium">{t.reports.active}</span>
                            <span className="text-[9px] text-slate-400">{t.reports.frequency}</span>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 md:px-5 py-4">
                        <div className="text-[9px] font-medium text-slate-500 uppercase tracking-wider mb-3">{t.reports.history}</div>
                        <div className="space-y-2">
                          {t.reports.historyRows.map((r, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                              <div>
                                <div className="text-[10px] text-slate-600 font-mono">{r.range}</div>
                                <div className="text-[9px] text-slate-400">{r.date}</div>
                              </div>
                              <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded border ${r.style}`}>{r.status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Optimizadores */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="lg:order-2">
              <h2 className="text-4xl md:text-5xl font-light text-slate-900 mb-8">
                {t.optimizers.title}
              </h2>
              <p className="text-xl text-slate-600 mb-6 leading-relaxed">
                {t.optimizers.p1}
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {t.optimizers.p2}
              </p>
              <ul className="space-y-4">
                {t.optimizers.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-slate-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:order-1 relative">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
                <div className="flex">
                  <div className="w-10 md:w-12 bg-slate-50 border-r border-slate-200 py-3 flex flex-col items-center gap-3 flex-shrink-0">
                    <div className="w-5 h-5 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-slate-200 rounded"></div>
                    <div className="w-4 h-4 bg-indigo-200 rounded"></div>
                  </div>
                  <div className="flex-1 min-w-0 p-4 md:p-5">
                    <div className="text-[11px] font-medium text-slate-700 mb-1">{t.optimizers.panelTitle}</div>
                    <div className="text-[9px] text-slate-400 mb-4">{t.optimizers.panelSubtitle}</div>
                    <div className="space-y-3">
                      {t.optimizers.items.map((opt, i) => (
                        <div key={i} className="border border-slate-200 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="text-[11px] font-medium text-slate-700">{opt.name}</div>
                              <div className="text-[9px] text-slate-400">{opt.type}</div>
                            </div>
                            <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded border ${opt.statusStyle}`}>{opt.status}</span>
                          </div>
                          <div className="text-[9px] text-slate-500 mb-2">{opt.desc}</div>
                          <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                            <span className="text-[9px] text-slate-400 font-mono">{opt.runs}</span>
                            <span className="text-[9px] text-indigo-600 font-medium">{opt.saving}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ClientLogos />

      <section id="demo" className="py-24 bg-gray-50">
        <ProductCTA
          productName="intelligence"
          features={t.ctaFeatures}
        />
      </section>

    </div>
  );
};

export default IntelligenceDetail;
